/**
 * @file SessionResolver.ts
 *
 * Module-level factory and singleton accessor for session contexts. This is the single
 * authoritative entry point for creating and retrieving the active recognition session —
 * no component or hook should instantiate `SessionContext` or `HybridModelSessionContext`
 * directly.
 *
 * Responsibilities:
 * - `createSessionContext()` — selects the correct implementation based on `modelType`:
 *     - `'hybridModel'` → `HybridModelSessionContext` (dual stable + quick recognition)
 *     - any other value → `SessionContext` (standard stable-only recognition)
 *   Only creates a new instance when no context exists or the current one is `STOPPED`.
 *   If called without full arguments and a context already exists, returns the existing one.
 *   Throws if called without arguments and no context has been created yet.
 * - `getSessionContext()` — returns the current singleton context (or `null` if none exists),
 *   used by hooks and components that need to interact with an already-running session.
 *
 * The module-level `currentSessionContext` variable acts as the singleton container.
 * Because a context transitions to `STOPPED` after `stop()` is called, the factory will
 * replace it transparently on the next `createSessionContext()` call, enabling session restart
 * without requiring a page reload.
 *
 * Pipeline position:
 *   OperatorApp / session hooks → SessionResolver (this file)
 *     → SessionContext | HybridModelSessionContext → recognition + TTS pipeline
 */
import {Input, Language, Output, TranslationResult, TranslationsBody} from './Configuration';
import {SessionContext} from './SessionContext';
import HybridModelSessionContext from './HybridModelSessionContext';
import {CancellationReason} from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports';
import {UUID} from 'crypto';

let currentSessionContext: SessionContext | HybridModelSessionContext | null;

export function createSessionContext(modelType: string,
                                     azureSpeechAPIToken?: string,
                                     sessionId?: UUID,
                                     selectedInput?: Input, selectedOutputs?: Output[],
                                     onPartialRecognitionReady?: (recognizedText: string) => void,
                                     onRecognitionReady?: (recognizedText: string) => void,
                                     onPartialTranslationReady?: (translationReady: Map<Language, TranslationResult>) => void,
                                     onTranslationReady?: (translationsBody: TranslationsBody) => void,
                                     onSessionCanceled?: (sessionId: UUID, azureSessionId: string, reason: CancellationReason, errorMsg: string) => void
): SessionContext | HybridModelSessionContext {

    if (!azureSpeechAPIToken || !sessionId || !selectedInput || !selectedOutputs || !onPartialRecognitionReady || !onRecognitionReady || !onPartialTranslationReady || !onTranslationReady || !onSessionCanceled) {
        if (currentSessionContext)
            return currentSessionContext;
        else
            throw new Error('SessionContext was not initialized')
    }

    if (!currentSessionContext || ('STOPPED' === currentSessionContext.getState())) {
        currentSessionContext = 'hybridModel' === modelType ?
            new HybridModelSessionContext(azureSpeechAPIToken, sessionId, selectedInput, selectedOutputs, onPartialRecognitionReady, onRecognitionReady, onPartialTranslationReady, onTranslationReady, onSessionCanceled) :
            new SessionContext(modelType, azureSpeechAPIToken, sessionId, selectedInput, selectedOutputs, onPartialRecognitionReady, onRecognitionReady, onPartialTranslationReady, onTranslationReady, onSessionCanceled);
    }
    return currentSessionContext;
}

export function getSessionContext(): SessionContext | HybridModelSessionContext | null {
    return currentSessionContext;
}
